import { DESCRIPTION_PLACEHOLDER_KEY } from '@/common/constants/constants';
import {
    Planner,
    PlannerWithDetails,
    Todo,
    TodoWithDetails,
} from '@/common/types/databaseTypes';
import { useCommentsCount } from '@/features/comment/services/useCommentQueries';
import CreateNoteButton from '@/features/note/components/CreateNoteButton';
import NoteListItem from '@/features/note/components/NoteListItem';
import { useNotesByTodoId } from '@/features/note/services/useNoteQueries';
import { useUpdatePlanner } from '@/features/planner/services/usePlannerQueries';
import { useCheckWorkspaceMembership } from '@/features/workspace/services/useWorkspaceQueries';
import { useAuthStore } from '@/stores/authStore';
import { useTodoDrawerStore } from '@/stores/todoDrawerStore';
import {
    ActionIcon,
    Box,
    Button,
    Divider,
    Group,
    Popover,
    PopoverDropdown,
    PopoverTarget,
    Stack,
    Text,
    Textarea,
    Tooltip,
} from '@mantine/core';
import {
    IconArrowsDiagonal,
    IconChevronRight,
    IconLock,
    IconLockOpen,
    IconNotes,
    IconTrash,
    IconX,
} from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTodo, useUpdateTodo } from '../../services/useTodoQueries';
import { CategoryMenu } from '../CategoryMenu';
import { TodoActionMenu } from '../TodoActionMenu';
import { TodoBasicInfo, TodoMetadata } from './TodoContentCommon';

interface TodoPopoverProps {
    opened: boolean;
    onClose: () => void;
    children?: React.ReactNode; // 팝오버 타겟
    planner?: Partial<PlannerWithDetails>;
    todo?: Partial<TodoWithDetails>; // 인박스, 끝내지 못한 할일 등 플래너가 없을 때 사용
    onSubmit?: (todoData: Partial<Todo>) => Promise<void>;
    onDataChange?: (todoData: Partial<Todo>) => void;
}

export const TodoPopover = ({
    opened,
    onClose,
    children,
    planner,
    todo,
    onSubmit, // close 시 호출
    onDataChange, // title, category 편집 시 호출
}: TodoPopoverProps) => {
    const { t } = useTranslation();
    const { user } = useAuthStore();
    const updatePlanner = useUpdatePlanner();
    const updateTodo = useUpdateTodo();
    const { openDrawer } = useTodoDrawerStore();

    const { data: todoData, error: todoError } = useTodo(
        planner?.todo?.id ?? null
    );

    const [isSubmitting, setIsSubmitting] = useState(false);

    // 새 할 일 또는 수정중인 할 일을 위한 로컬 상태
    const [localTodoData, setLocalTodoData] = useState<
        Partial<TodoWithDetails>
    >({
        id: null,
        title: '',
        category_id: null,
        is_public: true,
        content: '',
        due_date: null,
        goal_id: null,
        cancelled_date: null,
        complete_date: null,
        goal: null,
    });

    // 서버에서 가져온 데이터로 로컬 상태 업데이트
    useEffect(() => {
        if (todoData) {
            setLocalTodoData(prev => ({
                id: todoData.id,
                title: todoData.title,
                category_id: todoData.category_id,
                is_public: todoData.is_public ?? true,
                content: todoData.content ?? '',
                due_date: todoData?.due_date,
                goal_id: todoData?.goal_id,
                cancelled_date: todoData?.cancelled_date,
                complete_date: todoData?.complete_date,
                goal: todoData?.goal,
            }));
        } else if (todo) {
            setLocalTodoData({
                id: todo.id,
                title: todo.title,
                category_id: todo.category_id,
                is_public: todo.is_public ?? true,
                content: todo.content ?? '',
                due_date: todo?.due_date,
                goal_id: todo?.goal_id,
                cancelled_date: todo?.cancelled_date,
                complete_date: todo?.complete_date,
                goal: todo?.goal,
            });
        }
    }, [todoData, todo]);
    const { data: todoNotes } = useNotesByTodoId(localTodoData?.id ?? '');
    const { data: todoCommentCount } = useCommentsCount(
        'todo',
        localTodoData?.id ?? ''
    );

    const { data: editPermission, isLoading: permissionLoading } =
        useCheckWorkspaceMembership(planner?.goal?.team_id);

    const isAccessable = editPermission?.isAllowed;

    const handleDeletePlannerRange = (
        plannerId: string,
        rangeToDelete: number[]
    ) => {
        if (!planner) {
            console.log('플래너가 없어 업데이트 불가');
            return;
        }

        const updateBlocks = planner?.blocks?.filter(
            block => !rangeToDelete.includes(block)
        );

        updatePlanner.mutate({
            id: plannerId,
            blocks: updateBlocks,
            update_id: user?.id ?? '',
        });

        onClose();
    };

    const handleUpdateTodo = (updates: Partial<Todo>) => {
        if (!localTodoData?.id) return;

        updateTodo.mutate({
            id: localTodoData?.id,
            ...updates,
            update_id: user?.id ?? '',
        });
    };

    // title이 있는 경우만 저장
    const updateData = async () => {
        // 로컬 상태에서 제목 가져오기 (기존 todoData가 없을 경우 대비)
        const title = localTodoData?.title?.trim();

        if (title) {
            // 기존 투두가 있으면 업데이트, 없으면 신규 추가
            if (localTodoData?.id) {
                handleUpdateTodo({
                    title,
                    category_id: localTodoData.category_id,
                    is_public: localTodoData.is_public,
                    content: localTodoData.content,
                    due_date: localTodoData.due_date,
                    goal_id: localTodoData.goal_id,
                    cancelled_date: localTodoData.cancelled_date,
                    complete_date: localTodoData.complete_date,
                });
            } else if (onSubmit && !isSubmitting) {
                // 신규 투두 추가 처리
                try {
                    setIsSubmitting(true);

                    await onSubmit({
                        title,
                        category_id: localTodoData.category_id,
                        is_public: localTodoData.is_public ?? true,
                        content: localTodoData.content,
                        due_date: localTodoData.due_date,
                        goal_id: localTodoData.goal_id,
                        cancelled_date: localTodoData.cancelled_date,
                        complete_date: localTodoData.complete_date,
                    });
                } catch (error) {
                    console.error('투두 추가 중 오류 발생:', error);
                } finally {
                    setIsSubmitting(false);
                }
            }
        }
    };

    // 팝오버를 닫을 때 호출되는 함수 - title이 있는 경우만 저장
    const handleClose = async () => {
        await updateData();
        onClose();
    };

    const handleCategoryChange = (categoryId: string | null) => {
        // 로컬 상태 업데이트
        setLocalTodoData(prev => ({
            ...prev,
            category_id: categoryId,
        }));

        // 서버에 이미 저장된 투두인 경우 업데이트 요청
        if (localTodoData?.id) {
            handleUpdateTodo({ category_id: categoryId });
        }

        // 새로 만들고 있을 때 플래너에 컬러 반영되게 onChange 호출
        if (onDataChange) {
            onDataChange({
                ...localTodoData,
                category_id: categoryId,
            });
        }
    };

    const handleTogglePublic = (e: React.MouseEvent) => {
        e.stopPropagation();
        const newIsPublic = !localTodoData.is_public;

        // 로컬 상태 업데이트
        setLocalTodoData(prev => ({
            ...prev,
            is_public: newIsPublic,
        }));

        // 서버에 이미 저장된 투두인 경우 업데이트 요청
        if (localTodoData?.id) {
            handleUpdateTodo({ is_public: newIsPublic });
        }
    };

    const handleTitleChange = (newTitle: string) => {
        // 로컬 상태 업데이트
        setLocalTodoData(prev => ({
            ...prev,
            title: newTitle,
        }));

        // 새로 만들고 있을 때 플래너에 타이틀 반영되게 onChange 호출
        if (onDataChange) {
            onDataChange({
                ...localTodoData,
                title: newTitle,
            });
        }
    };

    const handleContentChange = (newContent: string) => {
        // 로컬 상태 업데이트
        setLocalTodoData(prev => ({
            ...prev,
            content: newContent,
        }));
    };

    const handleDueDateChange = (newDueDate: string | null) => {
        // 로컬 상태 업데이트
        setLocalTodoData(prev => ({
            ...prev,
            due_date: newDueDate,
        }));

        // 서버에 이미 저장된 투두인 경우 업데이트 요청
        if (localTodoData?.id) {
            handleUpdateTodo({ due_date: newDueDate });
        }
    };

    const handleGoalChange = (goalId: string | null) => {
        // 로컬 상태 업데이트
        setLocalTodoData(prev => ({
            ...prev,
            goal_id: goalId,
        }));

        // 서버에 이미 저장된 투두인 경우 업데이트 요청
        if (localTodoData?.id) {
            handleUpdateTodo({ goal_id: goalId });
        }
    };

    const handlePlannerSelect = (planner: Partial<Planner>) => {
        if (!localTodoData?.id) return;

        onClose();
        openDrawer({
            todoId: localTodoData?.id,
            targetUserId: planner?.user_id ?? todo?.user_id ?? user?.id ?? '', // 누구의 플래너를 보여줄지 결정 (target user id)
            initialView: 'timeBlock',
            plannerData: planner,
            date: new Date(planner.planner_date),
        });
        // setTodoDetailOpened(true);
    };

    // 새 투두를 위한 임시 객체 생성 (todoData가 없을 경우 사용)
    const todoForDisplay =
        todoData ||
        ({
            ...localTodoData,
            id: localTodoData?.id || null,
            title: localTodoData.title || '',
            content: localTodoData.content || '',
            user_id: user?.id,
            register_id: user?.id,
            update_id: user?.id,
            cancelled_date: null,
            complete_date: null,
            goal: localTodoData?.goal || null,
        } as Todo);

    return (
        <Popover
            onDismiss={() => void handleClose()}
            width="400px"
            opened={opened}
            position="left"
            trapFocus
            shadow="lg"
            radius="lg"
            closeOnClickOutside
            closeOnEscape
        >
            {children && <PopoverTarget>{children}</PopoverTarget>}
            <PopoverDropdown
                onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                }}
                // inert 속성을 사용하지 않고 다른 방법으로 처리
                onKeyDown={
                    !isAccessable
                        ? e => {
                              // 키보드 이벤트 차단
                              e.stopPropagation();
                              e.preventDefault();
                          }
                        : undefined
                }
            >
                {/* 접근 권한이 없을 때 마우스 및 키보드 상호작용 차단 */}
                {!isAccessable && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 1000,
                            cursor: 'not-allowed',
                            backgroundColor: 'rgba(255, 255, 255, 0.01)', // 오버레이가 보이지 않도록 거의 투명하게 처리
                        }}
                        // 이 속성이 Tab 키 포커스를 차단하는 핵심 구현
                        tabIndex={0}
                        aria-hidden="true"
                        onKeyDown={e => {
                            // Tab키를 포함한 모든 키보드 이벤트를 차단
                            e.stopPropagation();
                            e.preventDefault();
                        }}
                    />
                )}
                <Stack gap="sm">
                    <Group justify="space-between">
                        <ActionIcon
                            tabIndex={-1}
                            size="sm"
                            variant="subtle"
                            color="gray"
                            onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                void handleClose();
                            }}
                        >
                            <IconX size="1rem" />
                        </ActionIcon>
                        <Group gap="xs">
                            <CategoryMenu
                                withinPortal={false}
                                tabIndex={-1}
                                categoryId={localTodoData?.category_id ?? null}
                                onChangeCategory={(
                                    categoryId: string | null
                                ) => {
                                    handleCategoryChange(categoryId);
                                }}
                            />
                            {/* 상세보기 버튼은 이미 투두가 있는 경우만 표시 */}
                            {localTodoData?.id && (
                                <Tooltip label={t('todo:component.todoDrawer.popover.view_detail')} withArrow>
                                    <ActionIcon
                                        size="sm"
                                        variant="default"
                                        tabIndex={-1}
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();

                                            // 수정된 내용이 있으면 먼저 저장
                                            if (
                                                localTodoData?.id &&
                                                localTodoData.content !==
                                                    todo?.content
                                            ) {
                                                void updateData();
                                            }

                                            onClose();

                                            // timeBlockContent에서 상태 유지가 안돼서 전역 방식으로 변경
                                            // setTodoDetailOpened(true);
                                            // setCloseCallback(() => {
                                            //     onClose();
                                            // });
                                            openDrawer({
                                                todoId: localTodoData.id,
                                                targetUserId:
                                                    planner?.user_id ??
                                                    todo?.user_id ??
                                                    user?.id ??
                                                    '',
                                            });
                                        }}
                                    >
                                        <IconArrowsDiagonal size="1rem" />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                            <Tooltip
                                label={t(
                                    'todo:component.todoDrawer.drawer.toggle_visibility',
                                    {
                                        status: t(
                                            `todo:component.todoDrawer.drawer.${localTodoData?.is_public ? 'private' : 'public'}`
                                        ),
                                    }
                                )}
                                withArrow
                            >
                                <ActionIcon
                                    size="sm"
                                    variant="default"
                                    tabIndex={-1}
                                    disabled={
                                        // 팀에 속한 투두는 비공개 전환 불가
                                        localTodoData?.goal?.team_id !==
                                        undefined
                                    }
                                    onClick={e => {
                                        handleTogglePublic(e);
                                    }}
                                >
                                    {localTodoData?.is_public ? (
                                        <IconLockOpen color="black" size={16} />
                                    ) : (
                                        <IconLock color="black" size={16} />
                                    )}
                                </ActionIcon>
                            </Tooltip>
                            {localTodoData?.id && (
                                <TodoActionMenu
                                    todo={localTodoData}
                                    planner={planner}
                                    withinPortal={false}
                                    onClose={onClose}
                                    menuIcon={<IconTrash size="1rem" />}
                                />
                            )}
                        </Group>
                    </Group>
                    <TodoBasicInfo
                        disabled={!isAccessable}
                        key={localTodoData?.id || 'new-todo'}
                        todo={
                            {
                                ...localTodoData,
                                id: localTodoData?.id,
                                title: localTodoData.title || '',
                            } as Todo
                        }
                        autoFocus={true}
                        onTitleChange={handleTitleChange}
                    />
                    {/* <TodoDescription
                        todo={{
                            ...todoForDisplay,
                            content: localTodoData.content || '',
                        }}
                        onContentChange={handleContentChange}
                    /> */}
                    <Textarea
                        readOnly={!isAccessable}
                        value={localTodoData.content || ''}
                        placeholder={t(DESCRIPTION_PLACEHOLDER_KEY)}
                        variant="unstyled"
                        autosize
                        styles={{
                            input: { paddingTop: '0' },
                        }}
                        leftSection={<IconNotes size={16} />}
                        leftSectionProps={{
                            style: {
                                display: 'flex',
                                alignItems: 'start',
                                justifyContent: 'start',
                                paddingTop: '3px',
                            },
                        }}
                        onBlur={() => {
                            const savedContent =
                                todo?.content || todoData?.content;

                            if (savedContent === localTodoData.content) return;

                            void updateData();
                        }}
                        // onKeyDown={e => {
                        //     if (e.key === 'Enter' || e.key === 'Escape') {
                        //         console.log('key down');
                        //         void updateData();
                        //     }
                        // }}
                        onChange={e => {
                            handleContentChange(e.target.value);
                        }}
                    />

                    <TodoMetadata
                        todo={todoForDisplay}
                        singlePlanner={planner}
                        onPlannerSelect={handlePlannerSelect}
                        onDueDateChange={handleDueDateChange}
                        onGoalChange={handleGoalChange}
                        isNewTodo={!localTodoData?.id}
                        onDeletePlannerRange={handleDeletePlannerRange}
                        withinPortal={false}
                    />

                    {/* 노트 리스트 및 상세보기 버튼은 이미 투두가 있는 경우만 표시 */}
                    {localTodoData?.id && (
                        <>
                            {todoNotes?.length > 0 && (
                                <>
                                    <Divider my={4} />
                                    <Text size="sm" c="gray" fw={500}>
                                        {t(
                                            'todo:component.todoDrawer.popover.notes'
                                        )}
                                    </Text>

                                    <Stack gap={4}>
                                        {todoNotes?.map(note => (
                                            <NoteListItem
                                                key={note?.id}
                                                noteId={note?.id}
                                                noteTitle={note?.title}
                                            />
                                        ))}
                                    </Stack>
                                </>
                            )}

                            <Divider />

                            <Group justify="space-between">
                                <CreateNoteButton todoId={localTodoData?.id} />
                                {todoCommentCount > 0 && (
                                    <Button
                                        variant="subtle"
                                        color="gray"
                                        size="xs"
                                        radius="md"
                                        styles={{
                                            section: {
                                                marginLeft: 4,
                                            },
                                        }}
                                        rightSection={
                                            <Box
                                                style={{
                                                    marginTop: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <IconChevronRight
                                                    size={12}
                                                    stroke={2.5}
                                                />
                                            </Box>
                                        }
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();

                                            // 수정된 내용이 있으면 먼저 저장
                                            if (
                                                localTodoData?.id &&
                                                localTodoData.content !==
                                                    todo?.content
                                            ) {
                                                void updateData();
                                            }

                                            onClose();

                                            openDrawer({
                                                todoId: localTodoData.id,
                                                targetUserId:
                                                    planner?.user_id ??
                                                    todo?.user_id ??
                                                    user?.id ??
                                                    '',
                                            });
                                        }}
                                    >
                                        {t(
                                            'todo:component.todoDrawer.popover.comments',
                                            { count: todoCommentCount }
                                        )}
                                    </Button>
                                )}
                            </Group>
                        </>
                    )}
                </Stack>
            </PopoverDropdown>
        </Popover>
    );
};
